import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, json } from "./api";
import type { GameDocument, SaveState } from "./types";

export function useDocument(
  initial: GameDocument,
  onSaved: (doc: GameDocument) => void,
) {
  const [doc, setDoc] = useState(initial),
    [state, setState] = useState<SaveState>("saved"),
    [message, setMessage] = useState(""),
    [conflict, setConflict] = useState<GameDocument | null>(null);
  const current = useRef(initial),
    revision = useRef(0),
    saved = useRef(0),
    inflight = useRef<Promise<boolean> | null>(null),
    timer = useRef<ReturnType<typeof setTimeout> | null>(null),
    conflictRef = useRef<GameDocument | null>(null),
    mounted = useRef(true),
    savedCallback = useRef(onSaved);
  savedCallback.current = onSaved;
  const flush = useCallback(async (): Promise<boolean> => {
    if (timer.current) clearTimeout(timer.current);
    if (conflictRef.current) return false;
    if (inflight.current) {
      if (!(await inflight.current)) return false;
    }
    if (saved.current === revision.current) return true;
    const promise = (async () => {
      while (saved.current !== revision.current) {
        const snapshot = current.current,
          edit = revision.current;
        if (!snapshot.title.trim()) {
          setState("error");
          setMessage("У документа должно быть название.");
          return false;
        }
        if (mounted.current) setState("saving");
        try {
          const result = await api<GameDocument>(
            `/projects/${snapshot.projectId}/documents/${snapshot.id}`,
            json("PUT", {
              title: snapshot.title,
              content: snapshot.content,
              folderId: snapshot.folderId,
              version: snapshot.version,
            }),
          );
          current.current = {
            ...current.current,
            version: result.version,
            updatedAt: result.updatedAt,
          };
          saved.current = edit;
          if (mounted.current) {
            setDoc({ ...current.current });
            setMessage("");
            setState(saved.current === revision.current ? "saved" : "dirty");
          }
          savedCallback.current(result);
        } catch (e) {
          const err = e as ApiError;
          if (mounted.current) {
            setMessage(err.message);
            if (err.status === 409 && err.data.current) {
              conflictRef.current = err.data.current as GameDocument;
              setConflict(conflictRef.current);
              setState("conflict");
            } else setState("error");
          }
          return false;
        }
      }
      return true;
    })();
    inflight.current = promise;
    try {
      return await promise;
    } finally {
      inflight.current = null;
    }
  }, []);
  const change = useCallback(
    (patch: Partial<GameDocument>) => {
      current.current = { ...current.current, ...patch };
      revision.current++;
      setDoc({ ...current.current });
      if (!conflictRef.current) {
        setState("dirty");
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => void flush(), 800);
      }
    },
    [flush],
  );
  useEffect(() => {
    mounted.current = true;
    const unload = (e: BeforeUnloadEvent) => {
      if (saved.current !== revision.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    const online = () => {
      if (!conflictRef.current) void flush();
    };
    window.addEventListener("beforeunload", unload);
    window.addEventListener("online", online);
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
      window.removeEventListener("beforeunload", unload);
      window.removeEventListener("online", online);
    };
  }, [flush]);
  function useServer() {
    const server = conflictRef.current;
    if (!server) return;
    current.current = server;
    saved.current = revision.current;
    conflictRef.current = null;
    setConflict(null);
    setDoc(server);
    setState("saved");
    setMessage("");
    savedCallback.current(server);
  }
  async function keepLocal() {
    const server = conflictRef.current;
    if (!server) return false;
    current.current = { ...current.current, version: server.version };
    conflictRef.current = null;
    setConflict(null);
    return flush();
  }
  return {
    doc,
    state,
    message,
    change,
    flush,
    conflict,
    useServer,
    keepLocal,
    getCurrent: () => current.current,
  };
}
